import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback,
} from "react";
import classes from "./index.module.css";
import PaginationTable from "../../components/Pagination";
import { AgGridReact } from "ag-grid-react";
import DefaultSelect from "../../components/form/DefaultSelect";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import DefaultInput from "../../components/form/DefaultInput";
import { Button, Modal } from "antd";
import { ColDef } from "ag-grid-community";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import useLang from "../../hooks/useLang";
import { useMediaQuery } from "@mui/material";

const BlockedAccounts = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [code, setCode] = useState<string | undefined>();
	const [tableData, setTableData] = useState<any[]>([]);
	const [openUnBlockingModal, setOpenUnBlockingModal] =
		useState<boolean>(false);

	let grid = useRef(null);

	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};

	const getTableData = async () => {
		try {
			const response = await api.get("User/fetch-blocked-accounts");
			if (response) {
				setTableData(response.data);
			}
		} catch (error) {
			console.log(error);
			setTableData([]);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	const { strings } = useLang("blockedAccounts");
	// Data loads once on open; the column effect below only relabels on a language switch.
	useEffect(() => {
		getTableData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.headerNameCode,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 80,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "userRoleDescription",
				headerName: strings?.headerNamePrivileges,
				width: 110,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.headerNameName,
				width: 150,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "phone",
				headerName: strings?.headerNamePhone,
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "email",
				headerName: strings?.headerNameEmail,
				// width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "country",
				headerName: strings?.headerNameCountry,
				width: 110,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.headerNameCity,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "address",
				headerName: strings?.headerNameAddress,
				// width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: "",
				cellRenderer: (params: any) => {
					return (
						<Button
							onClick={() => handleOpenModal(params.data.id)}
							className={classes.tableButtonFirst}
						>
							{strings?.unblock}
						</Button>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [setTableColumns, strings]);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const totalItems = tableData.length;

	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = Math.min(startIndex + pageSize, totalItems);

	const filteredData = useMemo(() => {
		return tableData.filter((item: any) => {
			return (
				!code ||
				(item.firstName &&
					item.firstName
						?.toLowerCase()
						.includes(code?.toLowerCase())) ||
				(item.lastName &&
					item.lastName
						?.toLowerCase()
						.includes(code?.toLowerCase())) ||
				(item.id &&
					item.id
						.toString()
						?.toLowerCase()
						.includes(code?.toLowerCase()))
			);
		});
	}, [code, tableData]);

	const paginatedBlockedAccounts = useMemo(() => {
		const dataToPaginate = filteredData;
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return dataToPaginate.slice(startIndex, endIndex);
	}, [currentPage, filteredData, pageSize]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const selectedColumnIds = [
		"id",
		"userRoleDescription",
		"firstName",
		"phone",
		"email",
		"country",
		"city",
		"address",
	];

	const onExportSelectedId = () => {
		const dataToExport = filteredData.map((item) => ({
			...item,
			firstName: `${item.firstName} ${item.lastName}`,
		}));
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};
	const [userToUnblock, setUserToUnblock] = useState<number | null>(null);

	const handleOpenModal = (userId: number) => {
		setUserToUnblock(userId);
		setOpenUnBlockingModal(true);
	};

	const handleCloseModal = () => {
		setOpenUnBlockingModal(false);
		setUserToUnblock(null);
	};

	const unblockUser = async () => {
		if (userToUnblock !== null) {
			try {
				await api.post("User/unblock-account", null, {
					params: { userId: userToUnblock },
				});
				toast.success(strings?.accountUnblockedSuccess);
				getTableData();
			} catch (error) {
				console.log(error);
			}
			setOpenUnBlockingModal(false);
			setUserToUnblock(null);
		}
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	return (
		<>
			<div className={classes.mainContainer}>
				{/* <FinanceStatistic /> */}

				<div className={classes.heroContainer}>
					<div className={classes.firstContainer}>
						<h1 className={classes.title}>
							{strings?.title}{" "}
							<span className={classes.clientsCount}>
								({tableData.length})
							</span>
						</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={onExportSelectedId}
							>
								<ExportIcon />
								{strings?.exportData}
							</Button>
						</div>
					</div>
					<div className={classes.inputs}>
						<DefaultInput
							label={strings?.search}
							className={classes.input}
							placeholder={strings?.searchPlaceholder}
							prefix={<SearchIcon />}
							value={code}
							onChange={(e: any) => {
								setCurrentPage(1);
								setCode(e.target.value);
							}}
							allowClear
						/>
						<DefaultSelect
							label="Shteti"
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.selectPlaceholder}
						/>
						<DefaultSelect
							label="Qyteti"
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.selectPlaceholder}
						/>
						<DefaultSelect
							label="Privilegjet"
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.selectPlaceholder}
						/>
					</div>
					<div className={classes.pagination}>
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={totalItems}
							startOrderIndex={startIndex}
							endOrderIndex={endIndex}
							onChange={handlePageChange}
							onShowSizeChange={handlePageSizeChange}
						/>
					</div>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							// rowData={filteredData}
							rowData={paginatedBlockedAccounts}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							onFirstDataRendered={onFirstDataRendered}
							// onCellClicked={onCellClicked}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={openUnBlockingModal}
				onCancel={handleCloseModal}
				onOk={unblockUser}
				title={strings?.modalTitle}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				<p>{strings?.modalContent}</p>
			</Modal>
		</>
	);
};

export default BlockedAccounts;
